/** Valyuta kursi: 1 USD = N UZS (Markaziy bank yoki qo'lda), 7 kunlik o'zgarish, o'rtacha o'lchamda 30 kunlik chiziq. */
import { useTranslation } from "react-i18next";
import { Landmark } from "lucide-react";
import { cn, formatDate } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useWidgetData } from "../data";
import { WidgetEmpty } from "../ui";

type Props = { item: WidgetItem<Record<string, never>>; me: Me };
interface Data {
  usd_uzs: string | null;
  date: string | null;
  source: "cbu" | "manual" | null;
  change_7d_pct: string | null;
  series: { date: string; usd_uzs: string }[];
}
const ENTITIES: string[] = [];

const fmtRate = (v: string | null) => (v === null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }));

/** 30 kunlik chiziq: SVG, tokenlardan (currentColor) */
function Sparkline({ series }: { series: Data["series"] }) {
  if (series.length < 2) return null;
  const values = series.map((p) => Number(p.usd_uzs));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 100;
  const h = 32;
  const points = values.map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-8 w-full text-ink" aria-hidden>
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Change({ pct }: { pct: string | null }) {
  const { t } = useTranslation("widgets");
  if (pct === null) return null;
  const n = Number(pct);
  return (
    <span className={cn("text-[11px] font-medium tabular-nums", n > 0 ? "text-danger" : n < 0 ? "text-success" : "text-muted")}>
      {n > 0 ? "+" : ""}
      {n.toFixed(2)}% · {t("rate.week")}
    </span>
  );
}

export function RateView({ item }: Props) {
  const { t } = useTranslation("widgets");
  const q = useWidgetData<Data>("rate", {}, ENTITIES);
  const d = q.data;
  if (!d) return q.isError ? <WidgetEmpty text={t("failed")} /> : null;
  const meta = d.date ? `${formatDate(d.date)} · ${t(d.source === "manual" ? "rate.manual" : "rate.cbu")}` : t("rate.none");
  if (item.size === "s") {
    return (
      <div className="flex h-full flex-col px-3 py-2.5">
        <div className="text-[11px] text-muted">{t("rate.usd")}</div>
        <div className="truncate text-[32px] leading-none font-semibold tracking-[-0.03em] tabular-nums">{fmtRate(d.usd_uzs)}</div>
        <div className="mt-1 text-[11px] text-muted">UZS</div>
        <div className="mt-auto flex flex-col">
          <Change pct={d.change_7d_pct} />
          <span className="truncate text-[10px] text-muted">{meta}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full gap-3 px-3 py-2.5">
      <div className="w-[128px] shrink-0">
        <div className="text-[11px] text-muted">{t("rate.usd")}</div>
        <div className="truncate text-[32px] leading-none font-semibold tracking-[-0.03em] tabular-nums">{fmtRate(d.usd_uzs)}</div>
        <div className="mt-1 text-[11px] text-muted">UZS</div>
        <div className="mt-2 flex flex-col">
          <Change pct={d.change_7d_pct} />
          <span className="truncate text-[10px] text-muted">{meta}</span>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-end border-l border-line pl-3">
        <Sparkline series={d.series} />
        <div className="mt-1 flex justify-between text-[10px] text-muted tabular-nums">
          <span>{d.series[0] ? formatDate(d.series[0].date) : ""}</span>
          <span>{t("rate.days", { count: d.series.length })}</span>
        </div>
      </div>
    </div>
  );
}

export function RateGlance() {
  const { t } = useTranslation("widgets");
  const q = useWidgetData<Data>("rate", {}, ENTITIES);
  return (
    <span className="inline-flex items-center gap-1.5" title={t("rate.title")}>
      <Landmark size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className="text-[12px] font-semibold tabular-nums">{q.data ? fmtRate(q.data.usd_uzs) : "–"}</span>
      <span className="text-[10px] text-muted">UZS</span>
    </span>
  );
}
